import React from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";

/**
 * Loading placeholder aligned with PeopleTable grid (checkbox + columns + actions).
 * @param {{ columns: Array<{ key: string, width?: string }>, rowCount?: number, leadWithAvatar?: boolean, selectable?: boolean }} props
 */
export default function PeopleTableSkeleton({
  columns,
  rowCount = 10,
  leadWithAvatar = false,
  selectable = true,
}) {
  const gridTemplate = [
    ...(selectable ? ["32px"] : []),
    ...columns.map((c) => c.width || "minmax(120px, 1fr)"),
    "minmax(52px, 64px)",
  ].join(" ");

  const rowKeys = Array.from({ length: rowCount }, (_, i) => i);

  const rowShell = {
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "center",
    minHeight: 64,
    borderBottom: "1px solid #EEF2F6",
    minWidth: "fit-content",
  };

  const headerShell = {
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "center",
    height: 48,
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: "#fff",
    minWidth: "fit-content",
    borderBottom: "1px solid #EEF2F6",
  };

  return (
    <Box
      sx={{
        mt: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
          scrollbarGutter: "stable",
          pb: "16px",
        }}
      >
        <Box sx={headerShell}>
          {selectable ? (
            <Skeleton variant="rectangular" width={16} height={16} sx={{ borderRadius: "4px" }} />
          ) : null}
          {columns.map((col) => (
            <Skeleton
              key={col.key}
              variant="text"
              width="55%"
              height={18}
              sx={{ borderRadius: "4px" }}
            />
          ))}
          <Box sx={{ minWidth: 0, display: "flex", justifyContent: "flex-end", pr: 0.5 }} />
        </Box>

        {rowKeys.map((i) => (
          <Box key={i} sx={rowShell}>
            {selectable ? (
              <Skeleton variant="rectangular" width={16} height={16} sx={{ borderRadius: "4px" }} />
            ) : null}
            {columns.map((col, idx) => (
              <Box key={col.key} sx={{ minWidth: 0, pr: 1 }}>
                {leadWithAvatar && idx === 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="text" width="75%" height={22} sx={{ borderRadius: "4px" }} />
                  </Box>
                ) : col.key === "status" ? (
                  <Skeleton variant="rounded" width={88} height={24} sx={{ borderRadius: "100px" }} />
                ) : (
                  <Skeleton
                    variant="text"
                    width={idx % 4 === 0 ? "92%" : "78%"}
                    height={20}
                    sx={{ borderRadius: "4px" }}
                  />
                )}
              </Box>
            ))}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                minWidth: 0,
                pr: 0.5,
              }}
            >
              <Skeleton variant="circular" width={28} height={28} />
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          width: "100%",
          background: "#fff",
          borderTop: "1px solid #E5E9EC",
          flexShrink: 0,
          pt: "16px",
          pb: "8px",
          px: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 1,
          }}
        >
          <Skeleton variant="text" width={140} height={22} />
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Skeleton variant="rounded" width={128} height={40} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" width={88} height={40} sx={{ borderRadius: "8px" }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
